import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import './AdminSettings.css';

const AdminSettings: React.FC = () => {
  const { user, refreshProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'preferences'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone_number: user?.phone_number || '',
  });

  // Update profile data when user changes
  useEffect(() => {
    if (user && !isEditing) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone_number: user.phone_number || '',
      });
    }
  }, [user, isEditing]);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const extractErrorMessage = (error: any, fallback: string): string => {
    const data = error?.response?.data;
    if (data?.message) return data.message;
    if (Array.isArray(data?.details) && data.details.length > 0) {
      const first = data.details[0];
      if (first?.msg) return first.msg;
    }
    if (data?.error) return data.error;
    return error?.message || fallback;
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      await authAPI.updateProfile({
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        email: profileData.email,
      });

      setMessage({ type: 'success', text: 'Profile updated successfully.' });
      setIsEditing(false);
      await refreshProfile();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: extractErrorMessage(error, 'Failed to update profile'),
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match.' });
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters.' });
      setLoading(false);
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.newPassword)) {
      setMessage({
        type: 'error',
        text: 'Password must include at least one uppercase letter, one lowercase letter, and one number.',
      });
      setLoading(false);
      return;
    }

    try {
      await authAPI.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });

      setMessage({ type: 'success', text: 'Password changed successfully.' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: extractErrorMessage(error, 'Failed to change password'),
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="admin-settings">
      <div className="settings-header">
        <h1>Account Settings</h1>
        <p>Manage your admin account profile and preferences</p>
      </div>

      {/* Tabs */}
      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          Profile
        </button>
        <button
          className={`settings-tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          Security
        </button>
        <button
          className={`settings-tab ${activeTab === 'preferences' ? 'active' : ''}`}
          onClick={() => setActiveTab('preferences')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3"/>
            <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
          </svg>
          Preferences
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`settings-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Tab Content */}
      <div className="settings-content">
        {activeTab === 'profile' && (
          <div className="settings-section">
            <div className="profile-card">
              <div className="profile-avatar-large">
                <span>{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
              </div>
              <h2>{user?.first_name} {user?.last_name}</h2>
              <p className="profile-role">Administrator</p>
              <p className="profile-joined">Admin since {formatDate(user?.created_at)}</p>
            </div>

            <div className="settings-form-card">
              <div className="form-header">
                <h3>Profile Information</h3>
                {!isEditing && (
                  <button
                    className="edit-btn"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Profile
                  </button>
                )}
              </div>

              {isEditing ? (
                <form onSubmit={handleProfileUpdate}>
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      value={profileData.first_name}
                      onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={profileData.last_name}
                      onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={profileData.phone_number}
                      readOnly
                      disabled
                    />
                    <small style={{ display: 'block', marginTop: '0.4rem', color: '#64748b', fontSize: '0.8rem' }}>
                      Phone numbers can't be changed here as they're used for login. Contact support if this needs to be updated.
                    </small>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setIsEditing(false);
                        setProfileData({
                          first_name: user?.first_name || '',
                          last_name: user?.last_name || '',
                          email: user?.email || '',
                          phone_number: user?.phone_number || '',
                        });
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="info-display">
                  <div className="info-row">
                    <span className="info-label">First Name</span>
                    <span className="info-value">{user?.first_name || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Last Name</span>
                    <span className="info-value">{user?.last_name || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Email Address</span>
                    <span className="info-value">{user?.email || 'Not provided'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Phone Number</span>
                    <span className="info-value">{user?.phone_number || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Account Created</span>
                    <span className="info-value">{formatDate(user?.created_at)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="settings-section">
            <div className="settings-form-card">
              <h3>Change Password</h3>
              <form onSubmit={handlePasswordChange}>
                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    required
                    placeholder="Enter your current password"
                  />
                </div>

                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    placeholder="Enter new password (min. 8 characters)"
                    minLength={8}
                  />
                </div>

                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                    placeholder="Confirm new password"
                    minLength={8}
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Changing Password...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>

            <div className="settings-form-card">
              <h3>Security Information</h3>
              <div className="info-display">
                <div className="info-row">
                  <span className="info-label">Phone Verification</span>
                  <span className={`status-badge ${user?.is_verified ? 'verified' : 'pending'}`}>
                    {user?.is_verified ? '✓ Verified' : 'Pending Verification'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Last Login</span>
                  <span className="info-value">{user?.last_login_at ? formatDate(user.last_login_at) : 'Never'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="settings-section">
            <div className="settings-form-card">
              <h3>Notification Preferences</h3>
              <div className="preference-item">
                <div className="preference-info">
                  <h4>Email Notifications</h4>
                  <p>Receive email alerts for new applications and payments</p>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" defaultChecked />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="preference-item">
                <div className="preference-info">
                  <h4>SMS Notifications</h4>
                  <p>Receive SMS alerts for critical system events</p>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" defaultChecked />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="preference-item">
                <div className="preference-info">
                  <h4>Weekly Reports</h4>
                  <p>Get weekly summary reports via email</p>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="settings-form-card">
              <h3>Admin Permissions</h3>
              <div className="info-display">
                <div className="permission-item">
                  <div className="permission-info">
                    <h4>Full Access</h4>
                    <p>You have full administrative access to all features</p>
                  </div>
                  <span className="permission-badge">Super Admin</span>
                </div>
                <div className="permission-list">
                  <div className="permission-tag">✓ Loan Approvals</div>
                  <div className="permission-tag">✓ User Management</div>
                  <div className="permission-tag">✓ Payment Processing</div>
                  <div className="permission-tag">✓ Document Verification</div>
                  <div className="permission-tag">✓ Reports & Analytics</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;

